import Image from "next/image";
import { ReactNode, useState } from "react";
import { FaBell, FaExclamationTriangle } from "react-icons/fa";
import { getLogoUrl } from "utils/getLogoUrl";

// Inlined SVGs (from public/images) so `stroke="currentColor"` picks up the
// color we set per category.
const MarketingIcon = ({ size, color }: { size: number; color: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color, display: "block" }}
  >
    <path d="m3 11 18-5v12L3 14v-3z" />
    <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
  </svg>
);

const NewFeatureIcon = ({ size, color }: { size: number; color: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color, display: "block" }}
  >
    <path d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z" />
    <path d="M19 14l.7 1.8 1.8.7-1.8.7L19 19l-.7-1.8-1.8-.7 1.8-.7L19 14z" />
  </svg>
);

// Truck — matches the carrier filter button's icon, in the stroke style of the
// other category SVGs.
const CarrierIcon = ({ size, color }: { size: number; color: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color, display: "block" }}
  >
    <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
    <path d="M15 18H9" />
    <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
    <circle cx="7" cy="18" r="2" />
    <circle cx="17" cy="18" r="2" />
  </svg>
);

export const CATEGORY_COLORS: Record<string, string> = {
  marketing: "#2f8fef",
  new_feature: "#1f9d6b",
  carrier_change: "#e8833a",
  platform_issue: "#e5484d",
};

const renderGlyph = (
  category: string,
  glyphSize: number,
  color: string
): ReactNode => {
  switch (category) {
    case "marketing":
      return <MarketingIcon size={glyphSize} color={color} />;
    case "new_feature":
      return <NewFeatureIcon size={glyphSize} color={color} />;
    case "carrier_change":
      return <CarrierIcon size={glyphSize} color={color} />;
    case "platform_issue":
      return <FaExclamationTriangle size={glyphSize} color={color} />;
    default:
      return <FaBell size={glyphSize} color={color} />;
  }
};

// The bare type glyph (no circle) — reused by the detail modal header.
export const CategoryGlyph = ({
  category,
  size,
  color,
}: {
  category: string;
  size: number;
  color: string;
}) => <>{renderGlyph(category, size, color)}</>;

interface NotificationIconProps {
  category: string;
  carrierName?: string | null;
  size?: number;
  read?: boolean;
}

/**
 * Carrier notifications show the carrier logo on a white circle. Other
 * categories show a glyph in a brand-colored circle: solid with a white glyph
 * when unread, a pale tint with the colored glyph when read.
 */
export const NotificationIcon = ({
  category,
  carrierName,
  size = 24,
  read = false,
}: NotificationIconProps) => {
  const [imageError, setImageError] = useState(false);

  if (carrierName && !imageError) {
    // White circle so transparent carrier logos stay legible in dark mode.
    return (
      <div
        className="flex items-center justify-center bg-white border rounded-full border-gray6"
        style={{ width: size, height: size }}
      >
        <Image
          src={getLogoUrl(carrierName)}
          alt={`${carrierName} logo`}
          width={Math.round(size * 0.7)}
          height={Math.round(size * 0.7)}
          className="object-contain"
          onError={() => setImageError(true)}
          unoptimized
        />
      </div>
    );
  }

  const color = CATEGORY_COLORS[category] ?? "#889096";

  return (
    <div
      className="flex items-center justify-center rounded-full"
      style={{
        width: size,
        height: size,
        // `${color}24` ≈ 14% alpha tint for the read state.
        backgroundColor: read ? `${color}24` : color,
      }}
    >
      {renderGlyph(category, Math.round(size * 0.58), read ? color : "#ffffff")}
    </div>
  );
};
